/**
 * =============================================================================
 * PLP (Product Listing Page) JavaScript
 * =============================================================================
 * Consolidated JavaScript for collection/product listing pages.
 * Replaces scattered code from: product-item.js, changeCollectionColor.js
 *
 * Note: CollectionSection class remains in theme.js as it has dependencies
 * on theme-specific utilities (Drawer, Popover, TimelineLite, etc.)
 */

function setProductInfoTagsMarkup(productInfoEl, rawText) {
  if (!productInfoEl) return;
  productInfoEl.innerHTML = '';
  const text = (rawText || '').trim();
  if (!text) return;
  const p = document.createElement('p');
  p.className = 'product-info-type';
  p.textContent = text;
  productInfoEl.appendChild(p);
}

/* =============================================================================
   ProductItem Web Component
   ============================================================================= */

/* Shared resize handler — one listener instead of per-component */
const _productItems = new Set();
let _sharedResizeTimeout;
let _cachedSlideWidth = null;

window.addEventListener('resize', () => {
  _cachedSlideWidth = null;
  clearTimeout(_sharedResizeTimeout);
  _sharedResizeTimeout = setTimeout(() => {
    _productItems.forEach(item => {
      if (!item._initialized) return;
      const wasMobile = item.isMobile;
      if (wasMobile !== item.isMobileView()) {
        item.init();
      }
    });
  }, 150);
}, { passive: true });

/* Shared page-scroll handler — one listener instead of per-component */
let _sharedScrollTimeout;
window.addEventListener('scroll', () => {
  clearTimeout(_sharedScrollTimeout);
  _sharedScrollTimeout = setTimeout(() => {
    _productItems.forEach(item => {
      if (item._initialized && item.isMobile) {
        item.resetMobileCarousel();
      }
    });
  }, 150);
}, { passive: true });

class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.INFINITE_LOOPS_COUNT = 1;
    this.LAYOUT_DELAY_MS = 40;
    this.MAX_SLIDES_MULTIPLIER = 10;

    this._activeImageIndex = 0;
    this.images = [];
    this.isHovered = false;
    this.isMobile = false;
    this._isInitializing = false;

    this._resizeHandler = null;
    this._mouseEnterHandler = null;
    this._mouseLeaveHandler = null;
    this._mobileScrollHandler = null;

    this._mobileMediaQuery = window.matchMedia('(max-width: 1007px)');
    this._cachedElements = {};
  }

  getInfiniteLoopCount() {
    const section = this.closest('section[data-use-infinite-scroll-wrapper]');
    if (!section) {
      return this.INFINITE_LOOPS_COUNT;
    }
    return section.getAttribute('data-use-infinite-scroll-wrapper') === 'true'
      ? this.INFINITE_LOOPS_COUNT
      : 0;
  }

  isMobileView() {
    return this._mobileMediaQuery.matches;
  }

  connectedCallback() {
    _productItems.add(this);

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.init();
          this._observer.disconnect();
          this._observer = null;
        }
      });
    }, { rootMargin: '200px' });

    this._observer.observe(this);
  }

  disconnectedCallback() {
    _productItems.delete(this);

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._mouseEnterHandler) {
      this.removeEventListener('mouseenter', this._mouseEnterHandler);
      this._mouseEnterHandler = null;
    }
    if (this._mouseLeaveHandler) {
      this.removeEventListener('mouseleave', this._mouseLeaveHandler);
      this._mouseLeaveHandler = null;
    }

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');
    if (scrollWrapper && this._mobileScrollHandler) {
      scrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    this.images = [];
    this._cachedElements = {};
  }

  init() {
    if (this._mouseEnterHandler) {
      this.removeEventListener('mouseenter', this._mouseEnterHandler);
      this._mouseEnterHandler = null;
    }
    if (this._mouseLeaveHandler) {
      this.removeEventListener('mouseleave', this._mouseLeaveHandler);
      this._mouseLeaveHandler = null;
    }

    const oldScrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');
    if (oldScrollWrapper && this._mobileScrollHandler) {
      oldScrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    const wasMobile = this.isMobile;
    const isNowMobile = this.isMobileView();

    if (wasMobile && !isNowMobile) {
      this.cleanupMobileMode();
    }

    this.images = Array.from(this.querySelectorAll('img.ProductItem__Image'));

    if (this.images.length === 0) {
      this.images = Array.from(this.querySelectorAll('img[data-media-id]'));
    }

    this._activeImageIndex = 0;
    this.isMobile = this.isMobileView();
    this._cachedElements = {};

    if (this.isMobile) {
      this.setupMobile();
    } else {
      this.setupDesktop();
    }

    this._initialized = true;
  }

  cleanupMobileMode() {
    const scrollWrappers = this.querySelectorAll('.ProductItem__ScrollWrapper');
    scrollWrappers.forEach(wrapper => wrapper.remove());

    const progressBars = this.querySelectorAll('.ProductItem__ProgressBar');
    progressBars.forEach(bar => bar.remove());

    const imageSlides = this.querySelectorAll('.ProductItem__ImageSlide');
    imageSlides.forEach(slide => slide.remove());

    const originalImages = this.querySelectorAll('.ProductItem__Image:not(.ProductItem__ImageSlide img)');
    originalImages.forEach(img => {
      if (img.classList.contains('ProductItem__Image--carousel')) {
        img.style.cssText = '';
        img.style.opacity = '0';
        img.classList.remove('active');
      } else {
        img.style.cssText = '';
      }
    });
  }

  setupDesktop() {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length > 1) {
      this._mouseEnterHandler = this.handleMouseEnter.bind(this);
      this._mouseLeaveHandler = this.handleMouseLeave.bind(this);

      this.addEventListener('mouseenter', this._mouseEnterHandler);
      this.addEventListener('mouseleave', this._mouseLeaveHandler);
      this.createArrows();
    }

    // Setup hover swatches (per Figma ROW-70)
    this.setupHoverSwatches();
  }

  /**
   * Setup hover swatches functionality for desktop
   * Allows users to switch product color variants on hover
   */
  setupHoverSwatches() {
    const hoverSwatches = this.querySelector('.ProductItem__HoverSwatches');
    if (!hoverSwatches) return;

    const swatchButtons = hoverSwatches.querySelectorAll('.ProductItem__HoverSwatch');
    if (swatchButtons.length <= 1) return;

    swatchButtons.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Update active state
        swatchButtons.forEach((s) => s.classList.remove('ProductItem__HoverSwatch--active'));
        swatch.classList.add('ProductItem__HoverSwatch--active');

        // Get variant image data
        const variantImageUrl = swatch.getAttribute('data-variant-image-url');
        if (variantImageUrl) {
          this.updateProductImage(variantImageUrl);
        }
      });
    });
  }

  /**
   * Update the main product image when a color swatch is clicked
   * @param {string} imageUrl - The URL of the variant image
   */
  updateProductImage(imageUrl) {
    const mainImage = this.querySelector('.ProductItem__Image:not(.ProductItem__Image--alternate):not(.ProductItem__Image--carousel)');
    if (mainImage) {
      mainImage.src = imageUrl;
      // Update srcset if present
      const srcset = mainImage.getAttribute('srcset');
      if (srcset) {
        const newSrcset = srcset.replace(/https:\/\/[^\s]+/g, (match) => {
          const sizeMatch = match.match(/_(\d+)x\./);
          if (sizeMatch) {
            return imageUrl.replace('_800x.', `_${sizeMatch[1]}x.`);
          }
          return imageUrl;
        });
        mainImage.setAttribute('srcset', newSrcset);
      }
    }
  }

  setupMobile() {
    if (this.images.length <= 1) return;

    const carouselArrowsEnabled =
      this.getAttribute('data-carousel-arrows-enabled') !== 'false';

    if (!carouselArrowsEnabled) return;

    const imageContainer = this.querySelector('.ProductItem__ImageContainer');
    const aspectRatio =
      imageContainer?.querySelector('.AspectRatio') || imageContainer;

    if (!aspectRatio) return;

    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );
    const naturalOrderImages = [...carouselImages];
    const { orderedImages, rolloverImageNumber } =
      this.buildImageSequence(carouselImages);
    const scrollWrapper = this.createScrollWrapperInfinite(
      naturalOrderImages,
      orderedImages,
    );

    this.rolloverImageNumber = rolloverImageNumber;
    this.setupSequenceCounts(carouselImages.length, orderedImages.length);

    const oldImages = Array.from(this.images);
    const mainImage = oldImages.find(img =>
      !img.classList.contains('ProductItem__Image--alternate') &&
      !img.classList.contains('ProductItem__Image--carousel')
    ) || oldImages[0];

    oldImages.forEach(img => {
      if (img !== mainImage) {
        img.style.opacity = '0';
      }
    });

    if (mainImage) {
      mainImage.style.position = 'absolute';
      mainImage.style.top = '0';
      mainImage.style.left = '0';
      mainImage.style.width = '100%';
      mainImage.style.height = '100%';
      mainImage.style.objectFit = 'cover';
      mainImage.style.zIndex = '10';
      mainImage.style.pointerEvents = 'none';
    }

    scrollWrapper.style.opacity = '0';
    aspectRatio.appendChild(scrollWrapper);
    this.images = Array.from(scrollWrapper.querySelectorAll('img'));

    if (this.prependedSlides > 0) {
      scrollWrapper.style.scrollBehavior = 'auto';
      const tempWidth = aspectRatio.offsetWidth || this.offsetWidth || 300;
      scrollWrapper.scrollLeft = this.prependedSlides * tempWidth;
    }

    this.setInitialScrollPosition(scrollWrapper);
    this.createProgressBarAfterImageWrapper();

    const showCarousel = () => {
      scrollWrapper.style.opacity = '1';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mainImage) {
            mainImage.style.display = 'none';
          }

          oldImages.forEach(img => {
            if (img !== mainImage) {
              img.style.display = 'none';
            }
          });
        });
      });
    };

    if (!this.prependedSlides || this.prependedSlides <= 0) {
      requestAnimationFrame(showCarousel);
    } else {
      this._showCarouselCallback = showCarousel;
    }

    this._mobileScrollHandler = this.handleMobileScroll.bind(this);
    scrollWrapper.addEventListener('scroll', this._mobileScrollHandler);
  }

  fixImageUrls(img) {
    ['src', 'srcset'].forEach((attr) => {
      const value = img.getAttribute(attr);

      if (value && value.includes('{width}')) {
        img.setAttribute(attr, value.replace(/{width}x/g, '600x'));
      }
    });
  }

  setInitialScrollPosition(scrollWrapper) {
    this.resetProgressBar();

    if (this.prependedSlides > 0) {
      this._isInitializing = true;
      scrollWrapper.style.opacity = '0';

      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollWrapper.style.scrollBehavior = 'auto';

          let slideWidth = _cachedSlideWidth;
          if (!slideWidth) {
            const container = scrollWrapper.parentElement;
            slideWidth = Math.round(container ? container.offsetWidth : 0);
            if (slideWidth === 0) {
              const productItem = this.querySelector('.ProductItem__ImageContainer');
              slideWidth = productItem ? Math.round(productItem.offsetWidth) : Math.round(this.offsetWidth);
            }
            _cachedSlideWidth = slideWidth;
          }

          scrollWrapper
            .querySelectorAll('.ProductItem__ImageSlide')
            .forEach((slide) => {
              slide.style.minWidth = (slideWidth + 1) + 'px';
              slide.style.width = (slideWidth + 1) + 'px';
            });

          scrollWrapper.scrollLeft = this.prependedSlides * slideWidth;
          this._activeImageIndex = this.prependedSlides;

          this.resetProgressBar();

          requestAnimationFrame(() => {
            scrollWrapper.style.scrollBehavior = '';
            this._isInitializing = false;

            if (this._showCarouselCallback) {
              this._showCarouselCallback();
              this._showCarouselCallback = null;
            } else {
              scrollWrapper.style.opacity = '1';
            }
          });
        }, this.LAYOUT_DELAY_MS);
      });
    }
  }

  /**
   * Reset progress bar fills to initial position
   */
  resetProgressBar() {
    const progressFill = this.querySelector('.ProductItem__ProgressFill:not(.ProductItem__ProgressFill--clone)');
    const progressFillClone = this.querySelector('.ProductItem__ProgressFill--clone');

    if (progressFill) {
      progressFill.style.left = '0%';
    }
    if (progressFillClone) {
      progressFillClone.style.left = '-100%';
    }
  }

  createSlideElement(img) {
    const slide = document.createElement('div');
    slide.className = 'ProductItem__ImageSlide';
    slide.style.scrollSnapAlign = 'start';
    slide.style.scrollSnapStop = 'always';

    const clonedImg = img.cloneNode(true);
    clonedImg.className = 'ProductItem__Image';
    clonedImg.setAttribute('loading', 'lazy');

    this.fixImageUrls(clonedImg);
    slide.appendChild(clonedImg);

    return slide;
  }

  buildImageSequence(carouselImages) {
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );
    const mainImage = carouselImages[0];
    const orderedImages = [];

    let rolloverImgIndex = -1;
    let rolloverImageNumber = 2;

    if (alternateImage) {
      const alternateUrl = alternateImage.getAttribute('src');

      rolloverImgIndex = carouselImages.findIndex(
        (img) =>
          img.getAttribute('src') === alternateUrl && img !== mainImage,
      );
      rolloverImageNumber = rolloverImgIndex + 1;
    }

    if (mainImage) {
      orderedImages.push(mainImage);
    }

    if (rolloverImgIndex !== -1) {
      orderedImages.push(carouselImages[rolloverImgIndex]);

      for (let i = rolloverImgIndex + 1; i < carouselImages.length; i++) {
        orderedImages.push(carouselImages[i]);
      }
    } else {
      for (let i = 1; i < carouselImages.length; i++) {
        orderedImages.push(carouselImages[i]);
      }
    }

    return { orderedImages, rolloverImageNumber };
  }

  createScrollWrapperInfinite(naturalOrderImages, orderedImages) {
    const scrollWrapper = document.createElement('div');
    scrollWrapper.className = 'ProductItem__ScrollWrapper';
    scrollWrapper.style.scrollSnapType = 'x mandatory';

    const loops = this.getInfiniteLoopCount();

    this.appendImagesToWrapper(
      scrollWrapper,
      naturalOrderImages,
      loops,
    );

    const firstVisibleSlideIndex = loops * naturalOrderImages.length;

    this.appendImagesToWrapper(scrollWrapper, orderedImages, 1, firstVisibleSlideIndex);

    this.appendImagesToWrapper(
      scrollWrapper,
      naturalOrderImages,
      loops,
    );

    this.prependedSlides = firstVisibleSlideIndex;

    return scrollWrapper;
  }

  appendImagesToWrapper(scrollWrapper, images, loopCount, firstVisibleSlideIndex = null) {
    for (let loop = 0; loop < loopCount; loop++) {
      images.forEach((img, imgIndex) => {
        const slide = this.createSlideElement(img);

        if (firstVisibleSlideIndex !== null &&
            scrollWrapper.children.length === firstVisibleSlideIndex &&
            loop === 0 && imgIndex === 0) {
          slide.classList.add('ProductItem__ImageSlide--first');
        }

        scrollWrapper.appendChild(slide);
      });
    }
  }

  setupSequenceCounts(carouselImagesCount, orderedImagesLength) {
    let firstLoopCount = 1;
    const rolloverImgIndex = this.rolloverImageNumber - 1;

    if (rolloverImgIndex !== -1) {
      firstLoopCount++;
      firstLoopCount += carouselImagesCount - rolloverImgIndex - 1;
    } else {
      firstLoopCount += carouselImagesCount - 1;
    }

    this.firstLoopImageCount = firstLoopCount;
    this.originalImageCount = carouselImagesCount;
    this.originalMobileImageCount = orderedImagesLength;
  }

  buildUrlSequence(validUrls, rolloverImageNumber) {
    const orderedUrls = [];
    let rolloverIndex = null;

    if (
      typeof rolloverImageNumber === 'number' &&
      rolloverImageNumber > 1 &&
      rolloverImageNumber <= validUrls.length
    ) {
      rolloverIndex = rolloverImageNumber - 1;
    } else if (validUrls.length > 1) {
      rolloverIndex = 1;
    }

    orderedUrls.push(validUrls[0]);

    if (
      rolloverIndex !== null &&
      rolloverIndex < validUrls.length &&
      rolloverIndex !== 0
    ) {
      orderedUrls.push(validUrls[rolloverIndex]);
    }

    if (rolloverIndex !== null) {
      for (let i = rolloverIndex + 1; i < validUrls.length; i++) {
        orderedUrls.push(validUrls[i]);
      }
    } else {
      for (let i = 1; i < validUrls.length; i++) {
        orderedUrls.push(validUrls[i]);
      }
    }

    return {
      orderedUrls,
      rolloverImageNumber: rolloverIndex !== null ? rolloverIndex + 1 : 2,
    };
  }

  createScrollWrapperFromUrls(naturalOrderUrls, orderedUrls) {
    const scrollWrapper = document.createElement('div');
    scrollWrapper.className = 'ProductItem__ScrollWrapper';
    scrollWrapper.style.scrollSnapType = 'x mandatory';

    const loops = this.getInfiniteLoopCount();

    this.appendUrlsToWrapper(scrollWrapper, naturalOrderUrls, loops);

    const firstVisibleSlideIndex = loops * naturalOrderUrls.length;
    this.appendUrlsToWrapper(scrollWrapper, orderedUrls, 1, firstVisibleSlideIndex);

    this.appendUrlsToWrapper(scrollWrapper, naturalOrderUrls, loops);

    this.prependedSlides = firstVisibleSlideIndex;
    return scrollWrapper;
  }

  appendUrlsToWrapper(scrollWrapper, urls, loopCount, firstVisibleSlideIndex = null) {
    for (let loop = 0; loop < loopCount; loop++) {
      urls.forEach((url, urlIndex) => {
        const slide = this.createSlideFromUrl(url);

        if (firstVisibleSlideIndex !== null &&
            scrollWrapper.children.length === firstVisibleSlideIndex &&
            loop === 0 && urlIndex === 0) {
          slide.classList.add('ProductItem__ImageSlide--first');
        }

        scrollWrapper.appendChild(slide);
      });
    }
  }

  createSlideFromUrl(url) {
    const slide = document.createElement('div');
    slide.className = 'ProductItem__ImageSlide';
    slide.style.scrollSnapAlign = 'start';
    slide.style.scrollSnapStop = 'always';

    const img = this.safeCreateImage();
    img.className = 'ProductItem__Image';
    img.setAttribute('loading', 'lazy');
    img.alt = '';
    img.src = url.replace('_{width}x.', '_800x.');

    slide.appendChild(img);

    return slide;
  }

  createArrows() {
    const container = this.querySelector('.ProductItem__ImageContainer');
    if (!container) return;

    const prevArrow = document.createElement('button');
    prevArrow.className = 'ProductItem__PrevArrow';
    prevArrow.style.opacity = '0';
    prevArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.86133 1.57653L1.6764 5.4999L5.86133 9.42327L5.14139 10.1912L0.137332 5.4999L5.14139 0.808594L5.86133 1.57653Z" fill="black"/>
      </svg>
    `;
    prevArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(-1);
    };

    const nextArrow = document.createElement('button');
    nextArrow.className = 'ProductItem__NextArrow';
    nextArrow.style.opacity = '0';
    nextArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.138672 9.42347L4.3236 5.5001L0.138671 1.57673L0.85861 0.808798L5.86267 5.5001L0.85861 10.1914L0.138672 9.42347Z" fill="black"/>
      </svg>
    `;
    nextArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(1);
    };

    container.appendChild(prevArrow);
    container.appendChild(nextArrow);
  }

  getRolloverImageIndex() {
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );

    if (alternateImage) {
      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );
      const alternateSrc = alternateImage.getAttribute('src');

      if (alternateSrc) {
        const matchBySrc = carouselImages.find(
          (img) => img.getAttribute('src') === alternateSrc,
        );

        if (matchBySrc) {
          return this.images.indexOf(matchBySrc);
        }
      }

      const alternateSrcset = alternateImage.getAttribute('srcset');

      if (alternateSrcset) {
        const matchBySrcset = carouselImages.find(
          (img) => img.getAttribute('srcset') === alternateSrcset,
        );

        if (matchBySrcset) {
          return this.images.indexOf(matchBySrcset);
        }
      }
    }

    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    return carouselImages.length > 0
      ? this.images.indexOf(carouselImages[0])
      : 0;
  }

  getRolloverImageNumber() {
    const productElement = this.closest('product-item');

    if (productElement) {
      const rolloverData = productElement.getAttribute(
        'data-rollover-image-number',
      );

      if (rolloverData) {
        const number = parseInt(rolloverData, 10);

        return isNaN(number) ? null : number;
      }
    }

    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );

    if (alternateImage) {
      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );
      const alternateSrc = alternateImage.getAttribute('src');

      if (alternateSrc) {
        const matchBySrc = carouselImages.find(
          (img) => img.getAttribute('src') === alternateSrc,
        );

        if (matchBySrc) {
          return carouselImages.indexOf(matchBySrc) + 1;
        }
      }
    }

    return null;
  }

  handleMouseEnter() {
    this.isHovered = true;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.showArrows();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.hideArrows();
  }

  changeImage(direction) {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length === 0) return;

    if (this._activeImageIndex === 0) {
      const rolloverIndex = this.getRolloverImageIndex();
      this._activeImageIndex = rolloverIndex;
    }

    const currentCarouselIndex = carouselImages.findIndex(
      (img) => this.images.indexOf(img) === this._activeImageIndex,
    );

    let newCarouselIndex;

    if (direction > 0) {
      newCarouselIndex = (currentCarouselIndex + 1) % carouselImages.length;
    } else {
      newCarouselIndex = currentCarouselIndex - 1;

      if (newCarouselIndex < 0) {
        newCarouselIndex = carouselImages.length - 1;
      }
    }

    this._activeImageIndex = this.images.indexOf(
      carouselImages[newCarouselIndex],
    );
    this.updateCarousel();
  }

  updateCarousel() {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    carouselImages.forEach((img) => img.classList.remove('active'));

    const activeImage = this.images[this._activeImageIndex];

    if (activeImage?.classList.contains('ProductItem__Image--carousel')) {
      activeImage.classList.add('active');
    }
  }

  showArrows() {
    this.toggleArrows('1');
  }

  hideArrows() {
    this.toggleArrows('0');
  }

  toggleArrows(opacity) {
    if (!this._cachedElements.arrows) {
      this._cachedElements.arrows = this.querySelectorAll(
        '.ProductItem__PrevArrow, .ProductItem__NextArrow',
      );
    }

    this._cachedElements.arrows.forEach(
      (arrow) => (arrow.style.opacity = opacity),
    );
  }

  /**
   * Creates the progress bar indicator below the image carousel (per Figma ROW-70)
   * Structure: .ProductItem__ProgressBar > .ProductItem__ProgressFill (x2 for looping)
   * Two fills are used to create seamless looping - as one exits right, the other enters left
   */
  createProgressBarAfterImageWrapper() {
    const totalImages = this.originalImageCount || this.firstLoopImageCount || this.images.length;
    if (totalImages <= 1) return;

    const existingBar = this.querySelector('.ProductItem__ProgressBar');
    if (existingBar) existingBar.remove();

    const progressBar = document.createElement('div');
    progressBar.className = 'ProductItem__ProgressBar';

    // Create two fills for seamless looping
    const progressFill = document.createElement('div');
    progressFill.className = 'ProductItem__ProgressFill';

    const progressFillClone = document.createElement('div');
    progressFillClone.className = 'ProductItem__ProgressFill ProductItem__ProgressFill--clone';

    // Set fill width based on number of images (e.g., 3 images = 33.33% width)
    const segmentWidth = 100 / totalImages;
    progressFill.style.width = `${segmentWidth}%`;
    progressFillClone.style.width = `${segmentWidth}%`;

    progressFill.style.left = '0%';
    progressFillClone.style.left = '-100%'; // Start off-screen to the left

    progressBar.appendChild(progressFill);
    progressBar.appendChild(progressFillClone);

    // Insert after ImageWrapper
    const imageWrapper = this.querySelector('.ProductItem__ImageWrapper');
    if (imageWrapper?.parentNode) {
      imageWrapper.parentNode.insertBefore(progressBar, imageWrapper.nextSibling);
    } else {
      const wrapper = this.querySelector('.ProductItem__Wrapper');
      if (wrapper) {
        wrapper.insertBefore(progressBar, wrapper.firstChild?.nextSibling);
      } else {
        this.appendChild(progressBar);
      }
    }
  }

  handleMobileScroll() {
    if (this._isInitializing) return;

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    const scrollLeft = scrollWrapper.scrollLeft;
    const imageWidth = scrollWrapper.scrollWidth / this.images.length;
    const currentIndex = Math.round(scrollLeft / imageWidth);

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }

    this._rafId = requestAnimationFrame(() => {
      this.updateProgressBarFromScroll(scrollLeft, imageWidth);

      if (currentIndex !== this._activeImageIndex) {
        this._activeImageIndex = currentIndex;
      }

      const totalImages = this.images.length;
      const sequenceLength = this.originalImageCount;

      if (
        this.getInfiniteLoopCount() > 0 &&
        currentIndex > totalImages - sequenceLength
      ) {
        this.addMoreSequences(scrollWrapper);
      }

      this._rafId = null;
    });
  }

  addMoreSequences(scrollWrapper) {
    if (this.addingSequences) return;
    if (this.getInfiniteLoopCount() === 0) return;

    const maxSlides = this.originalImageCount * this.MAX_SLIDES_MULTIPLIER;

    if (!this._cachedElements.slideCount) {
      this._cachedElements.slideCount = scrollWrapper.querySelectorAll(
        '.ProductItem__ImageSlide',
      ).length;
    }

    const currentSlideCount = this._cachedElements.slideCount;

    if (currentSlideCount >= maxSlides) {
      return;
    }

    this.addingSequences = true;

    const secondLoopCount = this.originalImageCount;

    if (
      !this._cachedElements.allSlides ||
      this._cachedElements.allSlides.length !== currentSlideCount
    ) {
      this._cachedElements.allSlides = Array.from(
        scrollWrapper.querySelectorAll('.ProductItem__ImageSlide'),
      );
    }

    const allSlides = this._cachedElements.allSlides;
    const prependedCount = this.getInfiniteLoopCount() * this.originalImageCount;
    const sequenceStart = prependedCount;

    let patternSlides = allSlides.slice(
      sequenceStart,
      sequenceStart + secondLoopCount,
    );

    if (patternSlides.length < secondLoopCount) {
      patternSlides = allSlides.slice(-secondLoopCount);
    }

    const slidesAfterAdd = currentSlideCount + patternSlides.length;
    if (slidesAfterAdd <= maxSlides) {
      const fragment = document.createDocumentFragment();

      patternSlides.forEach((slide) => {
        const newSlide = slide.cloneNode(true);

        fragment.appendChild(newSlide);
      });
      scrollWrapper.appendChild(fragment);

      this.images = Array.from(scrollWrapper.querySelectorAll('img'));
      this._cachedElements.slideCount = slidesAfterAdd;
      this._cachedElements.allSlides = null;
    }

    setTimeout(() => {
      this.addingSequences = false;
    }, 100);
  }

  /**
   * Update progress bar position based on active image index (per Figma ROW-70)
   * The fill moves horizontally to indicate which image is currently visible
   */
  updateProgressBar() {
    const progressFill = this.querySelector('.ProductItem__ProgressFill:not(.ProductItem__ProgressFill--clone)');
    const progressFillClone = this.querySelector('.ProductItem__ProgressFill--clone');
    if (!progressFill) return;

    const totalImages = this.originalImageCount || this.images.length;
    if (!totalImages) return;

    const adjustedIndex = this._activeImageIndex - (this.prependedSlides || 0);
    const segmentWidth = 100 / totalImages;

    // Calculate position using modulo for looping
    const normalizedIndex = ((adjustedIndex % totalImages) + totalImages) % totalImages;
    const leftPosition = normalizedIndex * segmentWidth;

    const previousLeft = parseFloat(progressFill.style.left) || 0;
    const isWrapping = Math.abs(leftPosition - previousLeft) > 50;

    if (isWrapping) {
      if (this._progressWrapping) return;
      const isForward = leftPosition < previousLeft;
      const wrapTarget = isForward ? 100 : -segmentWidth;
      const cloneWrapTarget = wrapTarget - 100;

      progressFill.style.left = `${wrapTarget}%`;
      if (progressFillClone) progressFillClone.style.left = `${cloneWrapTarget}%`;

      this._progressWrapping = true;
      clearTimeout(this._wrapTimeout);
      this._wrapTimeout = setTimeout(() => {
        progressFill.style.transition = 'none';
        if (progressFillClone) progressFillClone.style.transition = 'none';
        progressFill.style.left = `${leftPosition}%`;
        if (progressFillClone) progressFillClone.style.left = `${leftPosition - 100}%`;
        requestAnimationFrame(() => {
          progressFill.style.transition = '';
          if (progressFillClone) progressFillClone.style.transition = '';
          this._progressWrapping = false;
        });
      }, 150);
      return;
    }

    if (this._progressWrapping) return;

    progressFill.style.left = `${leftPosition}%`;

    if (progressFillClone) {
      progressFillClone.style.left = `${leftPosition - 100}%`;
    }
  }

  updateProgressBarFromScroll(scrollLeft, imageWidth) {
    const progressFill = this.querySelector('.ProductItem__ProgressFill:not(.ProductItem__ProgressFill--clone)');
    const progressFillClone = this.querySelector('.ProductItem__ProgressFill--clone');
    if (!progressFill) return;

    const totalImages = this.originalImageCount || this.images.length;
    if (!totalImages) return;

    const segmentWidth = 100 / totalImages;

    const currentScrollIndex = scrollLeft / imageWidth;
    const adjustedIndex = currentScrollIndex - (this.prependedSlides || 0);

    const normalizedPosition = ((adjustedIndex % totalImages) + totalImages) % totalImages;
    const leftPosition = normalizedPosition * segmentWidth;

    const previousLeft = parseFloat(progressFill.style.left) || 0;
    const isWrapping = Math.abs(leftPosition - previousLeft) > 50;

    if (isWrapping) {
      if (this._progressWrapping) return;
      const isForward = leftPosition < previousLeft;
      const wrapTarget = isForward ? 100 : -segmentWidth;
      const cloneWrapTarget = wrapTarget - 100;

      progressFill.style.left = `${wrapTarget}%`;
      if (progressFillClone) progressFillClone.style.left = `${cloneWrapTarget}%`;

      this._progressWrapping = true;
      clearTimeout(this._wrapTimeout);
      this._wrapTimeout = setTimeout(() => {
        progressFill.style.transition = 'none';
        if (progressFillClone) progressFillClone.style.transition = 'none';
        progressFill.style.left = `${leftPosition}%`;
        if (progressFillClone) progressFillClone.style.left = `${leftPosition - 100}%`;
        requestAnimationFrame(() => {
          progressFill.style.transition = '';
          if (progressFillClone) progressFillClone.style.transition = '';
          this._progressWrapping = false;
        });
      }, 150);
      return;
    }

    if (this._progressWrapping) return;

    progressFill.style.left = `${leftPosition}%`;

    if (progressFillClone) {
      progressFillClone.style.left = `${leftPosition - 100}%`;
    }
  }



  resetMobileCarousel() {
    const targetIndex = this.prependedSlides || 0;

    if (!this.isMobile || this._activeImageIndex === targetIndex) return;

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    this.cleanupExcessSlides(scrollWrapper);

    const slideWidth = scrollWrapper.scrollWidth / this.images.length;
    const targetPosition = targetIndex * slideWidth;

    scrollWrapper.scrollTo({ left: targetPosition, behavior: 'smooth' });

    this._activeImageIndex = targetIndex;
    this.updateProgressBar();
  }

  cleanupExcessSlides(scrollWrapper) {
    const allSlides = scrollWrapper.querySelectorAll(
      '.ProductItem__ImageSlide',
    );

    const minKeep = this.originalImageCount * 2;
    const originalStructureLength = (this.prependedSlides || 0) + minKeep;

    if (allSlides.length > originalStructureLength) {
      for (let i = originalStructureLength; i < allSlides.length; i++) {
        allSlides[i].remove();
      }

      this.images = Array.from(scrollWrapper.querySelectorAll('img'));
    }
  }

  safeCreateImage() {
    try {
      return document.createElement('img');
    } catch (e) {
      try {
        const img = new Image();
        return img;
      } catch (e2) {
        const div = document.createElement('div');
        div.innerHTML = '<img>';
        return div.firstChild;
      }
    }
  }

  createCleanImage(templateImg) {
    const img = templateImg ? templateImg.cloneNode(false) : this.safeCreateImage();
    ['srcset', 'data-widths', 'data-sizes', 'sizes', 'data-src'].forEach(attr =>
      img.removeAttribute(attr)
    );
    return img;
  }

  updateImages(imageUrls, rolloverImageNumber = null) {
    const wasHovered = this.isHovered;

    let container = this.querySelector('.ProductItem__ImageContainer.AspectRatio');
    if (!container) {
      container = this.querySelector('.AspectRatio');
      if (!container) return;
    }

    const oldScrollWrapper = container.querySelector('.ProductItem__ScrollWrapper');
    if (oldScrollWrapper && this._mobileScrollHandler) {
      oldScrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    const validUrls = imageUrls.filter((url) => url);
    if (validUrls.length === 0) return;

    this._activeImageIndex = 0;
    this.isMobile = this.isMobileView();

    if (this.isMobile) {
      // Select only product images, not swatch images inside .product__colors
      const oldImages = Array.from(container.querySelectorAll('img:not(.product__colors img)'));
      const oldArrows = Array.from(container.querySelectorAll('.ProductItem__PrevArrow, .ProductItem__NextArrow'));
      const oldWrappers = Array.from(container.querySelectorAll('.ProductItem__ScrollWrapper'));
      const oldProgressBars = Array.from(this.querySelectorAll('.ProductItem__ProgressBar'));

      [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => {
        el.style.display = 'none';
      });

      const { orderedUrls, rolloverImageNumber: finalRolloverNumber } =
        this.buildUrlSequence(validUrls, rolloverImageNumber);
      const scrollWrapper = this.createScrollWrapperFromUrls(
        validUrls,
        orderedUrls,
      );

      this.rolloverImageNumber = finalRolloverNumber;
      this.setupSequenceCounts(validUrls.length, orderedUrls.length);

      scrollWrapper.style.opacity = '0';

      container.appendChild(scrollWrapper);

      if (this.prependedSlides > 0) {
        scrollWrapper.style.scrollBehavior = 'auto';
        const containerWidth = container.offsetWidth || this.offsetWidth || 300;
        scrollWrapper.scrollLeft = this.prependedSlides * containerWidth;
      }

      this.images = Array.from(scrollWrapper.querySelectorAll('img'));

      this.createProgressBarAfterImageWrapper();

      const showNewCarousel = () => {
        scrollWrapper.style.opacity = '1';

        requestAnimationFrame(() => {
          [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => {
            el.remove();
          });
        });
      };

      if (this.prependedSlides > 0) {
        this._showCarouselCallback = showNewCarousel;
        this.setInitialScrollPosition(scrollWrapper);
      } else {
        this._isInitializing = true;
        scrollWrapper.scrollLeft = 0;

        this.resetProgressBar();

        setTimeout(() => {
          showNewCarousel();
          this._isInitializing = false;
        }, this.LAYOUT_DELAY_MS);
      }

      this._mobileScrollHandler = this.handleMobileScroll.bind(this);
      scrollWrapper.addEventListener('scroll', this._mobileScrollHandler);
    } else {
      // Select only product images, not swatch images inside .product__colors
      const oldImages = Array.from(container.querySelectorAll('img:not(.product__colors img)'));
      const oldArrows = Array.from(container.querySelectorAll('.ProductItem__PrevArrow, .ProductItem__NextArrow'));
      const oldWrappers = Array.from(container.querySelectorAll('.ProductItem__ScrollWrapper'));
      const oldProgressBars = Array.from(this.querySelectorAll('.ProductItem__ProgressBar'));

      [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => el.remove());

      const alternateIndex = (rolloverImageNumber && rolloverImageNumber > 1 && rolloverImageNumber <= validUrls.length)
        ? rolloverImageNumber - 1
        : (validUrls.length > 1 ? 1 : null);

      const templateImg = container.querySelector('img') || document.querySelector('img');

      // Get the ImageWrapper to update the withAlternateImage class
      const imageWrapper = this.querySelector('.ProductItem__ImageWrapper');

      // Create main image FIRST (must be before alternate for CSS stacking)
      const mainImg = this.createCleanImage(templateImg);
      mainImg.className = 'ProductItem__Image';
      mainImg.src = validUrls[0].replace('_{width}x.', '_800x.');
      mainImg.setAttribute('loading', 'eager');
      mainImg.alt = '';
      container.appendChild(mainImg);

      // Create alternate image AFTER main image (for CSS hover effect)
      if (alternateIndex !== null) {
        const alternateImg = this.createCleanImage(templateImg);
        alternateImg.className = 'ProductItem__Image ProductItem__Image--alternate';
        alternateImg.src = validUrls[alternateIndex].replace('_{width}x.', '_800x.');
        alternateImg.setAttribute('loading', 'eager');
        alternateImg.alt = '';
        container.appendChild(alternateImg);

        // Add the class that enables hover effect
        if (imageWrapper) {
          imageWrapper.classList.add('ProductItem__ImageWrapper--withAlternateImage');
        }
      } else {
        // Remove the class if no alternate image
        if (imageWrapper) {
          imageWrapper.classList.remove('ProductItem__ImageWrapper--withAlternateImage');
        }
      }

      validUrls.forEach((url) => {
        const img = this.createCleanImage(templateImg);
        img.className = 'ProductItem__Image ProductItem__Image--carousel';
        img.src = url.replace('_{width}x.', '_800x.');
        img.setAttribute('loading', 'lazy');
        img.alt = '';
        container.appendChild(img);
      });

      this.images = Array.from(this.querySelectorAll('img.ProductItem__Image'));
      if (this.images.length === 0) {
        this.images = Array.from(this.querySelectorAll('img[data-media-id]'));
      }

      this.init();

      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );

      if (carouselImages.length > 1) {
        this.showArrows();
      }

      if (wasHovered) {
        this.isHovered = true;
      }
    }
  }
}

// TheRow.min.js loads before this file and usually registers product-item first.
if (!customElements.get('product-item')) {
  customElements.define('product-item', ProductItem);
} else {
  const ProductItemCtor = customElements.get('product-item');
  const plpMediaQuery = window.matchMedia('(max-width: 1007px)');

  ProductItemCtor.prototype.isMobileView = function () {
    return plpMediaQuery.matches;
  };

  document.querySelectorAll('product-item').forEach((el) => {
    if (el._initialized) {
      el.init();
    }
  });
}

/* =============================================================================
   Color Swatch Handler (Collection Color Change)
   ============================================================================= */
(() => {

  // Currency formatter
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });

  /**
   * Handle legacy image update for non-carousel products (vanilla JS version)
   */
  const handleLegacyImageUpdateVanilla = (productElement, { title, imageUrl, altImageUrl }) => {
    const imageWrapper = productElement.querySelector('.ProductItem__ImageWrapper');
    const image = productElement.querySelector('.ProductItem__Image:not(.ProductItem__Image--alternate):not(.ProductItem__Image--carousel)');
    let altImage = productElement.querySelector('.ProductItem__Image--alternate');
    const imageContainer = productElement.querySelector('.ProductItem__ImageContainer');

    if (!altImage && altImageUrl && imageContainer) {
      const altImg = document.createElement('img');
      altImg.className = 'ProductItem__Image ProductItem__Image--alternate';
      altImg.src = altImageUrl.replace('_{width}x.', '_800x.');
      altImg.srcset = altImageUrl;
      altImg.alt = title || '';
      altImg.loading = 'eager';
      imageContainer.appendChild(altImg);
      altImage = altImg;
    }

    if (altImageUrl && altImage) {
      altImage.src = altImageUrl.replace('_{width}x.', '_800x.');
      altImage.srcset = altImageUrl;
      if (imageWrapper) {
        imageWrapper.classList.add('ProductItem__ImageWrapper--withAlternateImage');
      }
    } else if (altImage) {
      altImage.remove();
      if (imageWrapper) {
        imageWrapper.classList.remove('ProductItem__ImageWrapper--withAlternateImage');
      }
    }

    if (image && imageUrl) {
      image.src = imageUrl.replace('_{width}x.', '_800x.');
      image.srcset = imageUrl;
    }
  };

  /**
   * Color swatch click handler - Vanilla JS with event delegation
   * Intercepts clicks on color swatches and updates the product tile instead of navigating
   * Uses capture phase to intercept before other handlers
   */
  const handleSwatchClick = (e) => {
    const swatchLink = e.target.closest('.product__colors a');
    if (!swatchLink) return;

    // Make sure we're on a PLP (collection or search page), not PDP
    // On PDP, swatches should navigate. On PLP, they should update the tile.
    const isOnPLP = document.querySelector('.ProductListWrapper') ||
                    document.querySelector('.CollectionMain') ||
                    document.querySelector('.template-collection') ||
                    document.body.classList.contains('template-collection') ||
                    document.body.classList.contains('template-search');

    // Also check if the swatch is inside a product recommendations section (should navigate)
    const isInRecommendations = swatchLink.closest('.ProductRecommendationsSection') ||
                                swatchLink.closest('.RecentlyViewedSection') ||
                                swatchLink.closest('.SimilarItemsSection') ||
                                swatchLink.closest('.StyledWithSection');

    if (!isOnPLP || isInRecommendations) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Don't do anything if already the current variant
    if (swatchLink.classList.contains('currentVariant')) {
      return;
    }

    // Get data attributes from the swatch
    const {
      product_id: productId,
      title,
      price,
      compareAtPrice,
      imageUrl,
      altImageUrl,
      url,
      productInfoTags,
      preorderLabel,
      images,
      rolloverImageNumber,
    } = swatchLink.dataset;

    // Find the parent product card container and the product-item element
    // Try multiple selectors to find the container
    let parentContainer = swatchLink.closest('[data-product-card-type]');
    if (!parentContainer) {
      // Fallback: try to find the product-item directly
      parentContainer = swatchLink.closest('product-item')?.parentElement;
    }
    if (!parentContainer) {
      // Last resort: use the ProductItem__Wrapper's parent
      parentContainer = swatchLink.closest('.ProductItem__Wrapper')?.parentElement?.parentElement;
    }
    if (!parentContainer) return;

    // Find the product element - try by ID first, then by closest product-item
    let productElement = parentContainer.querySelector(`#productId_${productId}`);
    if (!productElement) {
      productElement = swatchLink.closest('product-item');
    }
    if (!productElement) return;

    // Match Liquid structure: .product-info-tags > .product-info-type (avoids layout jump vs plain text)
    const productInfo = productElement.querySelector('.product-info-tags');
    setProductInfoTagsMarkup(productInfo, productInfoTags);

    const imageWrapperLinkForPreorder = productElement.querySelector('a.ProductItem__ImageWrapper');
    if (imageWrapperLinkForPreorder) {
      let statusLabel = imageWrapperLinkForPreorder.querySelector('.ProductItem__StatusLabel');
      const preorderText = (preorderLabel || '').trim();
      if (preorderText) {
        if (!statusLabel) {
          statusLabel = document.createElement('span');
          statusLabel.className = 'ProductItem__StatusLabel';
          const imageContainer = imageWrapperLinkForPreorder.querySelector('.ProductItem__ImageContainer');
          if (imageContainer) {
            imageWrapperLinkForPreorder.insertBefore(statusLabel, imageContainer);
          } else {
            imageWrapperLinkForPreorder.insertBefore(statusLabel, imageWrapperLinkForPreorder.firstChild);
          }
        }
        statusLabel.textContent = preorderText;
      } else if (statusLabel) {
        statusLabel.remove();
      }
    }

    // Update images using the ProductItem web component if available
    if (images && productElement.tagName.toLowerCase() === 'product-item' && productElement.updateImages) {
      try {
        const imageUrls = images.split('||').filter((u) => u?.trim());
        if (imageUrls.length > 0) {
          const rolloverNumber = rolloverImageNumber ? parseInt(rolloverImageNumber, 10) : null;
          productElement.updateImages(imageUrls, rolloverNumber);
        } else {
          handleLegacyImageUpdateVanilla(productElement, { title, imageUrl, altImageUrl });
        }
      } catch (err) {
        handleLegacyImageUpdateVanilla(productElement, { title, imageUrl, altImageUrl });
      }
    } else {
      handleLegacyImageUpdateVanilla(productElement, { title, imageUrl, altImageUrl });
    }

    // Remove currentVariant class from all swatches in this product card
    productElement.querySelectorAll('.product__colors .currentVariant').forEach(el => el.classList.remove('currentVariant'));
    parentContainer.querySelectorAll('.product__colors .currentVariant').forEach(el => el.classList.remove('currentVariant'));

    // Add currentVariant to clicked swatch
    swatchLink.classList.add('currentVariant');

    // Sync currentVariant to matching swatch in other location (desktop/mobile)
    const clickedUrl = swatchLink.getAttribute('href');
    if (clickedUrl) {
      parentContainer.querySelectorAll(`.product__colors a[href="${clickedUrl}"]`).forEach(el => {
        el.classList.add('currentVariant');
      });
    }

    // Update product links (image wrapper and title)
    const imageWrapperLink = productElement.querySelector('a.ProductItem__ImageWrapper');
    if (imageWrapperLink && url) {
      imageWrapperLink.href = url;
    }

    const titleLink = productElement.querySelector('.ProductItem__Title a');
    if (titleLink) {
      if (url) titleLink.href = url;
      if (title) titleLink.textContent = title;
    }

    // Update price display
    const priceHighlight = productElement.querySelector('.Price--highlight .tlab-currency-format');
    if (priceHighlight && price) {
      priceHighlight.textContent = formatter.format(price / 100);
    }

    const priceCompareAt = productElement.querySelector('.Price--compareAt .tlab-currency-format');
    if (priceCompareAt) {
      priceCompareAt.textContent = compareAtPrice && compareAtPrice != 0
        ? formatter.format(compareAtPrice / 100)
        : '';
    }
  };

  document.addEventListener('click', handleSwatchClick, true);
})();

function initContentCardAutoplayVideos(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const videos = scope.querySelectorAll
    ? scope.querySelectorAll('.content-card__video')
    : [];
  if (!videos.length) return;

  const tryPlay = (v) => {
    if (!v || v.tagName !== 'VIDEO') return;
    const st = window.getComputedStyle(v);
    if (st.display === 'none' || st.visibility === 'hidden') return;
    v.muted = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    const p = v.play();
    if (p && typeof p.then === 'function') p.catch(function () {});
  };

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) tryPlay(entry.target);
      });
    },
    { rootMargin: '80px', threshold: 0.01 }
  );

  videos.forEach((v) => {
    if (v.dataset.contentCardVideoInit) return;
    v.dataset.contentCardVideoInit = '1';
    io.observe(v);
    v.addEventListener('loadeddata', function onData() {
      v.removeEventListener('loadeddata', onData);
      tryPlay(v);
    });
    if (v.readyState >= 2) tryPlay(v);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initContentCardAutoplayVideos(document);
  });
} else {
  initContentCardAutoplayVideos(document);
}
document.addEventListener('shopify:section:load', function (e) {
  if (e && e.target) initContentCardAutoplayVideos(e.target);
});

/* =============================================================================
   Sibling market collection redirect (client)
   — when country/locale changes on the same collection URL, Liquid may not
   re-execute; ?country= can appear or update after load. See therow-plp-sibling-data.
   ============================================================================= */
(function therowPlpMarketClientRedirect() {
  const dataEl = document.getElementById('therow-plp-sibling-data');
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  let map = data.siblingMap;
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map);
    } catch (e) {
      return;
    }
  }
  if (!map || typeof map !== 'object' || Array.isArray(map)) return;

  /** EU country ISO → map key "EU" (map uses US / EU / JP, not FR / DE) */
  const EU_ISO = new Set(
    'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE,IS,LI,NO,CH,GB,UK,MC,SM,VA,AX,GF,GP,MQ,RE,YT,GI'
      .split(',')
  );

  function pathCollectionHandle() {
    const m = /\/collections\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : null;
  }

  /**
   * `?country=JP` (or `country_code=`) — normalized uppercase, empty string if absent.
   * Used for tracking + target resolution.
   */
  function getCountryQuery() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('country') || params.get('country_code');
    if (!raw) return '';
    return String(raw).toUpperCase();
  }

  /**
   * Map key (US, EU, JP) for a ?country= ISO code, or null
   */
  function mapKeyForQueryCountry(iso) {
    if (!iso) return null;
    const c = String(iso).toUpperCase();
    if (Object.prototype.hasOwnProperty.call(map, c)) return c;
    if (map.EU && EU_ISO.has(c)) return 'EU';
    return null;
  }

  /**
   * Only `?country=` / `country_code=` can drive a client redirect. Do not use
   * theme.region / serverRegion here — otherwise markets without a map key (e.g. HK)
   * can be sent to the wrong sibling (e.g. EU).
   */
  function targetMapKey() {
    const q = getCountryQuery();
    if (!q) return null;
    return mapKeyForQueryCountry(q);
  }

  function isSiblingPathHandle(h) {
    if (!h) return false;
    return Object.keys(map).some((k) => map[k] === h);
  }

  let lastRedirected = null;
  let lastSeenHref = window.location.href;
  let lastSeenCountryQuery = getCountryQuery();

  function run() {
    const pathHandle = pathCollectionHandle();
    if (!pathHandle || !isSiblingPathHandle(pathHandle)) return;

    const key = targetMapKey();
    if (!key) return;

    const targetHandle = map[key];
    if (!targetHandle || targetHandle === pathHandle) return;

    const token = pathHandle + '→' + targetHandle + window.location.search;
    if (lastRedirected === token) return;
    lastRedirected = token;

    const newPath = window.location.pathname.replace(
      /(\/collections\/)([^/?#]+)/,
      '$1' + targetHandle
    );
    if (newPath === window.location.pathname) return;

    window.location.replace(
      newPath + window.location.search + window.location.hash
    );
  }

  /**
   * React when the full href changes *or* only `?country=` / `?country_code=` (e.g. JP) changes.
   * Some flows update the query without other differences being obvious to host code.
   */
  function onLocationOrCountryQueryChanged() {
    const h = window.location.href;
    const cq = getCountryQuery();
    if (h === lastSeenHref && cq === lastSeenCountryQuery) return;
    lastSeenHref = h;
    lastSeenCountryQuery = cq;
    run();
  }

  /**
   * Shopify / theme often updates market via history.pushState / replaceState — no
   * full reload, no popstate. Patch both + poll href as a fallback.
   */
  (function patchHistoryForPlp() {
    const p = history.pushState;
    const r = history.replaceState;
    history.pushState = function () {
      const res = p.apply(this, arguments);
      queueMicrotask(onLocationOrCountryQueryChanged);
      return res;
    };
    history.replaceState = function () {
      const res = r.apply(this, arguments);
      queueMicrotask(onLocationOrCountryQueryChanged);
      return res;
    };
  })();

  // First paint + late ?country= / theme (same tick edge cases)
  run();
  [0, 50, 150, 400, 1000, 2000, 3000, 5000, 10000, 20000, 30000].forEach(function (ms) {
    setTimeout(run, ms);
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) onLocationOrCountryQueryChanged();
  });
  // Back/forward: full URL from cache
  window.addEventListener('popstate', onLocationOrCountryQueryChanged);

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') onLocationOrCountryQueryChanged();
  });
  window.addEventListener('focus', onLocationOrCountryQueryChanged);

  // pushState / replaceState patched above; poller for href / ?country= changes
  let pollerN = 0;
  const pollT = window.setInterval(function () {
    pollerN += 1;
    onLocationOrCountryQueryChanged();
    if (pollerN >= 200) {
      clearInterval(pollT);
    }
  }, 400);
})();